import * as React from "react";

import { cn } from "@/core/utils";
import { buildWhatsappUrl } from "@/core/contact/whatsapp";

import { buttonVariants } from "./button";

/**
 * Botón que abre WhatsApp (deep-link `wa.me`) con el número —y opcionalmente
 * un texto— precargado. Renderiza un `<a>` simple (sin estado, server-safe):
 * se puede usar tanto en Server como en Client Components.
 *
 * Si el teléfono no es utilizable devuelve `null` (degradación elegante: no
 * mostramos un botón roto). El texto se codifica en el helper, así que no hay
 * riesgo de inyección en la URL.
 */
export function WhatsappButton({
  phone,
  message,
  label = "WhatsApp",
  variant = "outline",
  size = "sm",
  className,
  iconOnly = false,
}: {
  phone: string | null | undefined;
  message?: string | null;
  label?: string;
  variant?: "default" | "outline" | "secondary" | "ghost" | "link";
  size?: "default" | "xs" | "sm" | "lg" | "icon" | "icon-xs" | "icon-sm";
  className?: string;
  iconOnly?: boolean;
}) {
  const url = buildWhatsappUrl(phone, message);
  if (!url) return null;

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={iconOnly ? label : undefined}
      title={iconOnly ? label : undefined}
      className={cn(
        buttonVariants({ variant, size }),
        // Acento verde WhatsApp, sutil para no romper la paleta teal.
        "text-emerald-700 hover:text-emerald-800 dark:text-emerald-400",
        className,
      )}
    >
      <WhatsappIcon />
      {!iconOnly && label}
    </a>
  );
}

/** Glifo de WhatsApp. Hereda el tamaño de los estilos de botón (`[&_svg]`). */
function WhatsappIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.821 11.821 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.515 5.26l-.999 3.648 3.74-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.017-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z" />
    </svg>
  );
}
